import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { StickyScrollFade } from '@/components/atoms/StickyScrollFade/StickyScrollFade';
import { MODAL_VIEWS } from '@/components/organisms/Modals/ModalConductor';
import { DeskDropdown } from '@/components/organisms/PageSubHeader/DeskDropdown';
import { useRtmViews } from '@/hooks/rtm/useRtmViews';
import useDesks from '@/hooks/useDesks';
import useUser from '@/hooks/useUser';
import { useViews } from '@/hooks/useViews';
import { cn } from '@/lib/utils';
import { pushModal } from '@/redux/modals/actions';
import { selectAccountSlug } from '@/redux/session/selectors';

import { ConversationsPrefetcher } from './ConversationsPrefetcher';
import { NavListContainer } from './NavListContainer';
import { ViewsItem } from './ViewsItem';
import { useNav } from '../../hooks/useNav';
import { setView } from '../../redux/actions';
import { selectNavReduxState } from '../../redux/navSelectors';
import { ResizeHandle } from '../MessagesList/ResizeHandle';
import { useResizablePanel } from '../MessagesList/useResizablePanel';

const MIN_WIDTH = 240;
const MAX_WIDTH = 512;
const DEFAULT_WIDTH = 320;
const STORAGE_KEY = 'humanChat.leftSidebar.width';

export const Nav = memo(() => {
  const { sideBarOpen, viewTitle } = useSelector(selectNavReduxState);
  const slug = useSelector(selectAccountSlug);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { user } = useUser();
  const {
    conversationsMenu,
    filteredTeammatesMenu,
    filteredDepartmentsMenu,
    teammatesMenu,
    departmentsMenu,
    pinnedAgents,
    pinnedDepartments,
    canReadOthers,
    canReadBrain,
    canReadDepartmentsOthers,
  } = useNav();
  const { views, isViewsLoading, dropdownOpen, canReadViews } = useViews();
  const { serviceDesks } = useDesks();

  const scrollRef = useRef<HTMLUListElement>(null);
  const [showFade, setShowFade] = useState(false);

  const {
    width: panelWidth,
    panelRef,
    isActive,
    separatorProps,
  } = useResizablePanel({
    minWidth: MIN_WIDTH,
    maxWidth: MAX_WIDTH,
    initialWidth: DEFAULT_WIDTH,
    resizeFrom: 'right',
    storageKey: STORAGE_KEY,
  });

  const checkOverflow = useCallback(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    const hasOverflow = el.scrollHeight > el.clientHeight;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 1;
    const next = hasOverflow && !atBottom;
    setShowFade((prev) => (prev === next ? prev : next));
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    checkOverflow();
    const observer = new ResizeObserver(checkOverflow);
    observer.observe(el);
    return () => observer.disconnect();
  }, [checkOverflow]);

  useRtmViews();

  const handleOnMoreViewsClick = useCallback(() => {
    dispatch(pushModal(MODAL_VIEWS));
  }, [dispatch]);

  useEffect(() => {
    if (user && viewTitle === 'me') {
      dispatch(setView({ selectedAgent: user?.user_id }));
    }
  }, [dispatch, viewTitle, user]);

  /**
   * Normalizes teammates menu items for the PinMenuPopover
   * Handles disabled pins logic for brain agents
   */
  const normalizeTeammatesForPinMenu = useMemo(() => {
    return teammatesMenu.map((mate) => {
      const isBrain = mate.id === 'brain';
      const isPinned = pinnedAgents.includes(mate.id) || isBrain;

      return {
        ...mate,
        isPinned,
        isDisabled: isBrain, // Brain agents cannot be unpinned
      };
    });
  }, [teammatesMenu, pinnedAgents]);

  /**
   * Normalizes departments menu items for the PinMenuPopover
   * Handles disabled pins logic for user departments
   */
  const normalizeDepartmentsForPinMenu = useMemo(() => {
    return departmentsMenu.map((dept) => {
      const isPinned = pinnedDepartments.includes(dept.id) || dept.pinned;

      return {
        ...dept,
        isPinned,
        isDisabled: dept.pinned, // Departments can always be pinned/unpinned
      };
    });
  }, [departmentsMenu, pinnedDepartments]);

  return (
    <div
      style={{ width: sideBarOpen ? panelWidth : 0 }}
      className={cn(
        'relative flex h-full flex-col overflow-hidden bg-subtle',
        !isActive && 'transition-[width] duration-300 ease-in'
      )}
      ref={panelRef}
    >
      <ConversationsPrefetcher />
      {!!serviceDesks?.length && (
        <div className="flex h-14 items-center border-b border-border bg-background px-2 [&>div]:ml-0 [&>div]:w-full">
          <DeskDropdown
            serviceDeskOnly
            showSingle
            getPath={(id) => `/${slug}/chats/${id}/conversations`}
          />
        </div>
      )}
      <div className="relative min-h-0 flex-1 overflow-hidden">
        <ul
          ref={scrollRef}
          onScroll={checkOverflow}
          className="flex h-full flex-col overflow-auto p-2"
        >
          <NavListContainer
            type="conversations"
            title={t('conversation.conversations')}
            pinnedItems={conversationsMenu}
            hasPermission
            open
          />
          {/* Teammates (incl. the AI Agent) are other agents' inboxes — hidden for
              agents who can read neither others' nor the AI agent's conversations. */}
          {(canReadOthers || canReadBrain) && (
            <NavListContainer
              type="teammates"
              title={t('conversation.teammates')}
              pinnedItems={filteredTeammatesMenu}
              items={normalizeTeammatesForPinMenu}
              hasPermission={canReadOthers}
              open={filteredTeammatesMenu.length > 0}
              plusTooltip={t('conversation.view_teammates_inbox')}
            />
          )}
          {/* Keep the section when the agent has own departments; hide only the
              "browse other departments" (+) button when they can't read others'. */}
          {(canReadDepartmentsOthers || filteredDepartmentsMenu.length > 0) && (
            <NavListContainer
              type="departments"
              title={t('conversation.departments')}
              items={normalizeDepartmentsForPinMenu}
              pinnedItems={filteredDepartmentsMenu}
              hasPermission={canReadDepartmentsOthers}
              open={filteredDepartmentsMenu.length > 0}
              plusTooltip={t('conversation.view_departments_inbox')}
            />
          )}
          {canReadViews && (
            <ViewsItem
              views={views}
              dropdownOpen={dropdownOpen}
              onMoreClick={handleOnMoreViewsClick}
              isViewsLoading={isViewsLoading}
            />
          )}
        </ul>
        <StickyScrollFade show={showFade} direction="bottom" className="bottom-0 h-16" />
      </div>
      {sideBarOpen && (
        <ResizeHandle
          separatorProps={separatorProps}
          isActive={isActive}
          label={t('conversation.resize_panel')}
          side="right"
        />
      )}
    </div>
  );
});

Nav.displayName = 'Nav';
